import { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { Fingerprint, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const SESSION_KEY = 'dp_passkey_dismissed';

function isCancellation(err: any): boolean {
  const code = err?.errors?.[0]?.code ?? '';
  return (
    code === 'passkey_cancelled' ||
    err?.name === 'NotAllowedError' ||
    err?.name === 'AbortError'
  );
}

function clerkErrMsg(err: any): string {
  return (
    err?.errors?.[0]?.longMessage ??
    err?.errors?.[0]?.message ??
    'Setup failed — make sure you\'re on the app\'s main domain and try again.'
  );
}

export function PasskeyEnrollBanner() {
  const { isSignedIn, user } = useUser();
  const [supported, setSupported] = useState(false);
  const [visible, setVisible]     = useState(false);
  const [loading, setLoading]     = useState(false);
  const [enrollError, setEnrollError] = useState<string | null>(null);

  useEffect(() => {
    if (
      typeof PublicKeyCredential !== 'undefined' &&
      typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function'
    ) {
      PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(setSupported)
        .catch(() => {});
    }
  }, []);

  const hasUser = !!user;
  const passkeysLen = user?.passkeys?.length ?? 0;

  useEffect(() => {
    if (!isSignedIn || !supported || !hasUser) { setVisible(false); return; }

    if (passkeysLen > 0) {
      setVisible(false);
      return;
    }

    const dismissedThisSession = sessionStorage.getItem(SESSION_KEY) === '1';
    setVisible(!dismissedThisSession);
  }, [isSignedIn, supported, hasUser, passkeysLen]);

  if (!visible || true) return null; // passkeys not enabled in production

  const handleEnable = async () => {
    if (!user) return;
    setLoading(true);
    setEnrollError(null);
    try {
      await user.createPasskey();
      setVisible(false);
    } catch (err: any) {
      if (isCancellation(err)) {
        // User dismissed the native biometric prompt — quiet is correct,
        // but do NOT set the session suppress flag so they can try again
        return;
      }
      // Real failure — surface the error and keep the banner open for retry.
      // Do NOT set SESSION_KEY so the user can try again next session too.
      setEnrollError(clerkErrMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    sessionStorage.setItem(SESSION_KEY, '1');
    setVisible(false);
  };

  return (
    <div
      className="fixed bottom-20 left-0 right-0 z-50 px-4"
      data-testid="passkey-enroll-banner"
    >
      <div className="bg-[#064E3B] rounded-2xl p-4 shadow-xl flex items-start gap-3">
        <Fingerprint className="w-6 h-6 text-[#FACC15] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white leading-snug">
            Enable biometric sign-in?
          </p>
          <p className="text-xs text-emerald-200 mt-0.5">
            Skip the code next time — one tap gets you in.
          </p>

          {enrollError && (
            <div className="flex items-start gap-1.5 mt-2" data-testid="passkey-enroll-error">
              <AlertCircle className="w-3.5 h-3.5 text-red-300 shrink-0 mt-0.5" />
              <p className="text-xs text-red-300 leading-snug">{enrollError}</p>
            </div>
          )}

          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              onClick={handleEnable}
              disabled={loading}
              className="bg-[#FACC15] text-[#064E3B] font-bold hover:bg-yellow-300 h-8 px-3 text-xs"
              data-testid="button-passkey-enable"
            >
              {loading
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : (enrollError ? 'Try Again' : 'Enable Biometrics')
              }
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleSkip}
              disabled={loading}
              className="text-emerald-200 hover:text-white hover:bg-transparent h-8 px-3 text-xs"
              data-testid="button-passkey-skip"
            >
              Not now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
